/**
 * KÜNDIGUNG · RAHMEN — die Rollen-Sicht, 1:1 auf dem Gerüst des Widerrufs.
 *
 * > **Justin, 26.09.2026:** *„es soll 1 zu 1 so aussehen wie das
 * > Widerrufsregister, auch wenn ich auf Lipperreihe gehe und die Detailsicht
 * > des Falles habe. Wirklich 1 zu 1, halt nur für die Kündigungslogiken."*
 *
 * ═══ 🔴 WAS HIER FEHLTE, UND WARUM ES AUFFIEL ══════════════════════════════
 * 📏 Am 26.09.2026 nebeneinandergelegt, beide Detailansichten **desselben
 * Vereins** (Lipperreihe hatte zu dem Zeitpunkt in beiden Registern einen Fall):
 *   Widerruf:  „NICHT DU — Der Fall wartet auf Kundenbetreuung und Finance."
 *   Kündigung: nichts dergleichen.
 * Und in der Übersicht trug die Kündigungs-Karte eine Marke, die aus dem
 * **Pfad** abgeleitet war (`/kb/…` ⇒ KB) statt aus der Rolle des Menschen.
 * 🔑 ***Eine Marke, die aus der Adresse folgt, sagt, wo man steht — nicht, was
 * man zu tun hat.***
 *
 * ⇒ Geteilt wird die **Reihenfolge** (`wartetAufStand`, vier Schritte), eigen
 * bleiben die **Sätze**. Ein „Ergebnis" ist beim Widerruf eine Feststellung des
 * Vertriebs, bei der Kündigung ein Rückgewinnungs-Stand.
 */

import { escapeHtml } from "../../lib/html";
import { appFormatDate } from "../../lib/dates";
import type { UserContext } from "../../api/types";
import type { KuendigungFall, RegisterZeile, Riegel } from "../../api/kuendigung";
import { RIEGEL_LABEL, RUECKGEWINNUNG_OPTIONEN } from "../../api/kuendigung";
import { HAKEN, istBl, wartetAufStand, type WRolle, type Wartend } from "../widerruf/w-rahmen";

/**
 * Die drei Einstiege — wie beim Widerruf: wer aus Finance kommt, bleibt dort.
 *
 * ═══ 🔴 DREI FASSUNGEN DERSELBEN FUNKTION, GEFUNDEN VOM WÄCHTER ════════════
 * 📏 Am 26.09.2026 meldete `tote-exporte.test.ts`: `kBasisAus` hat **keinen
 * Aufrufer**. Der Grund war nicht, dass niemand sie braucht — sondern dass
 * `kuendigung-uebersicht.ts` und `kuendigung-detail.ts` je eine **eigene**
 * `basisAus` mit einem eigenen `K_BASEN` hatten. Drei Fassungen, und die hier
 * zählte die Basen in **anderer Reihenfolge** auf.
 *
 * ⚠️ Folgenlos war das nur zufällig: `find` prüft `p === b || p.startsWith(b + "/")`,
 * und `/kb/kuendigungen` beginnt nicht mit `/kuendigungen`. Bei einer vierten
 * Basis, die Präfix einer anderen ist, hätten zwei Dateien verschieden
 * geantwortet.
 * 🔑 ***Zwei Fassungen derselben Regel sind zwei Wahrheiten — es sei denn, eine
 * Prüfung hält sie zusammen.*** Hier hat sie es getan, und zwar über den
 * Umweg „unbenutzt": *eine ungenutzte Fassung ist die, die als Erste falsch wird.*
 */
export const K_BASEN = ["/kuendigungen", "/kb/kuendigungen", "/finance/kuendigungen"] as const;

export function kBasisAus(pfad: string): (typeof K_BASEN)[number] {
  const p = pfad.replace(/^#/, "");
  return K_BASEN.find((b) => p === b || p.startsWith(`${b}/`)) ?? K_BASEN[0];
}

/**
 * Die Rolle — Adresse **plus Berechtigung**, genau wie beim Widerruf.
 *
 * 🔑 Die vierte Rolle `kb_ma` ist keine Feinheit: für die Kundenbetreuung
 * quittiert **nur die Bereichsleitung**. Ohne die Unterscheidung zählte das
 * Band Arbeit mit, die ein KB-Mitarbeiter gar nicht erledigen kann.
 */
export function kRolleAus(basis: string, user: UserContext): WRolle {
  if (basis === "/finance/kuendigungen") return "finance";
  if (basis === "/kb/kuendigungen") {
    return user.isAdmin || istBl(user, "kundenbetreuung") ? "kb" : "kb_ma";
  }
  return "vertrieb";
}

const RIEGEL_PARTEIEN: readonly Riegel[] = ["vertrieb", "kb", "finance"];

/**
 * **Genau die Felder, die die Rollenfrage liest** — nicht der ganze Fall.
 *
 * 🔑 ***Ein `as KuendigungFall` hätte dasselbe erlaubt und nichts garantiert:***
 * wer morgen ein neuntes Feld in `wartetAufKuendigung` liest, bekäme in der
 * Übersichtszeile still `undefined` — und **kein Test schlägt an**, weil die
 * Tests vollständige Fälle bauen. Als eigener Typ ist derselbe Griff ein
 * Übersetzungsfehler an der Stelle, die ihn verursacht.
 * (Vorlage: `RiegelStand` im Widerruf.)
 */
export type KuendigungStand = Pick<KuendigungFall,
  "ende_bei_anlage" | "rueckgewinnung" | "rueckgewinnung_wiedervorlage"
  | "quittung_vertrieb" | "quittung_kb"
  | "quittung_finance" | "bestaetigung_am" | "storniert_am" | "abgeschlossen_am">;

function quittungAm(f: KuendigungStand, p: Riegel): string {
  return p === "vertrieb" ? f.quittung_vertrieb : p === "kb" ? f.quittung_kb : f.quittung_finance;
}

function rueckgewinnungLabel(wert: string): string {
  return RUECKGEWINNUNG_OPTIONEN.find((o) => o.wert === wert)?.label ?? wert;
}

/**
 * Wartet dieser Vorgang auf mich? `null` = nein.
 *
 * ⚠️ **Die Uhr ist eine andere als beim Widerruf.** Dort läuft eine
 * Bearbeitungsfrist (3 Werktage); hier läuft **das Vertragsende**. Ein
 * Kündigungs-Vorgang wird nicht „fristig", er wird **fällig**: ab dem Ende ist
 * die Abwicklung überfällig, und genau das mahnt auch der Melder.
 * 🔑 *Dieselbe Reihenfolge, eine andere Uhr — deshalb die Sätze hier und nicht
 * im geteilten Gerüst.*
 */
export function wartetAufKuendigung(
  f: KuendigungStand, rolle: WRolle, heute: string
): Wartend | null {
  const ende = String(f.ende_bei_anlage ?? "").trim();
  const faellig = !!ende && ende <= heute;

  /**
   * ② Ohne Ergebnis: Vertrieb trägt es ein, die Betreuung sieht zu.
   * Finance wartet erst danach — wie beim Widerruf auf das Ergebnis.
   *
   * ═══ 🔴 BEFUND 04.10.2026 — DIESE ZWEI SÄTZE ERREICHT NIEMAND ══════════════
   *
   * `kbSatz` landet unten in `ohneStand.kb` und `ohneStand.kb_ma`. Gelesen wird
   * diese Tabelle aber nur, wenn `vorbedingungOffen` **wahr** ist
   * (`w-rahmen.ts:438`), und das ist hier
   * `nurVertriebGesperrt = !f.rueckgewinnung && rolle === "vertrieb"`.
   * ⇒ Für `kb` und `kb_ma` ist die Bedingung **nie** wahr; sie bekommen immer
   * den Quittungs-Satz. 📏 Nachgemessen am 04.10. über alle vier Rollen und
   * beide Vertragsende-Lagen: `kb` liefert achtmal „Quittieren", nie diesen Satz.
   *
   * 🔑 ***Gefunden hat das die Gegenprobe, nicht der Test:*** ich habe den
   * Knopftext absichtlich zurückgedreht, und der Test blieb **grün**. Ein Test,
   * der grün bleibt, obwohl der Fehler drinsteht, erreicht die Stelle nicht —
   * und hier erreicht sie auch die Anwendung nicht.
   *
   * ✅ **ENTSCHIEDEN UND GESTRICHEN — Justin, 04.10.2026.**
   * Die Alternative wäre gewesen, `vorbedingungOffen` von der Rolle zu lösen.
   * ⛔ Das wäre **nicht** neutral: die Kundenbetreuung bekäme dann „Noch kein
   * Ergebnis — der Vertrieb trägt es ein" **statt** „deine Quittung fehlt" —
   * und sie **darf** quittieren. Genau das war der Entscheid vom 26.09.
   * 🔑 *Eine Sperre, die zu breit angezeigt wird, hält Leute von Arbeit ab,
   * die sie tun dürfen — und sie sieht dabei aus wie Sorgfalt.*
   * ⇒ `ohneStand` trägt nur noch den **Vertrieb**, für den die Vorbedingung
   * überhaupt gilt.
   */
  const ohneStand: Partial<Record<WRolle, Wartend>> = {
    vertrieb: faellig
      ? {
        grund: `Das Vertragsende <b>${escapeHtml(appFormatDate(ende))}</b> ist erreicht, und es steht `
          + "kein Ergebnis da.",
        cta: "Ergebnis melden"
      }
      : ende
        ? {
          grund: `Ergebnis offen — ohne es gibt es keinen Vertriebs-Haken. `
            + `Der Vertrag läuft bis <b>${escapeHtml(appFormatDate(ende))}</b>.`,
          cta: "Ergebnis melden"
        }
        : {
          // 🔑 Ohne Vertragsende läuft **keine** Uhr — und das gehört gesagt,
          // nicht verschwiegen. Dieselbe Lage wie „keine Frist" beim Widerruf.
          grund: "Ergebnis offen. ⚠️ Es steht <b>kein Vertragsende</b> dabei: "
            + "an diesen Fall erinnert niemand.",
          cta: "Ergebnis melden"
        }
  };

  /**
   * ═══ 🔴 DIE VORBEDINGUNG GILT NUR FÜR DEN VERTRIEB ═════════════════════
   *
   * > **Justin, 26.09.2026:** *„warum sind liperreihe und marienhagen bei
   * > finance nicht auf offen, ist ja bisher nicht quittiert"*
   *
   * Er hat recht, und der Fehler ist meiner. 📏 Nachgesehen in
   * `kuendigung-faelle.ts`: `if (riegel === "vertrieb" && !fall.rueckgewinnung)`
   * — **der Server sperrt genau EINEN Riegel.** KB und Finance dürfen jederzeit
   * quittieren; die HubSpot-Aufgabe an die KB sagt das sogar wörtlich
   * („Bitte im Kündigungsregister quittieren, sobald die Betreuung geregelt ist").
   *
   * Beim Widerruf ist es anders: dort sperrt das fehlende Ergebnis **alle**
   * Quittungen. Ich habe beim 1:1 das **Verhalten des Vorbilds** übernommen,
   * statt die **Regel des eigenen Servers** zu lesen — und Finance verschwand
   * aus seiner eigenen Arbeitsliste.
   * 🔑 ***Ein 1:1 überträgt die FORM, nicht die Zuständigkeiten*** — die stehen
   * im Server, und dort muss man sie holen.
   *
   * ⚠️ Der eigene Widerspruch stand die ganze Zeit daneben: `trio()` im
   * Falldetail rechnet `gesperrt = p === "vertrieb" && !f.rueckgewinnung` — also
   * richtig. Zwei Stellen derselben Regel, eine falsch.
   * 🔑 *Die zweite Fassung einer Regel ist die falsche, und man erkennt sie
   * daran, dass niemand die beiden nebeneinandergelegt hat.*
   *
   * ⛔ `kb_ma` fällt damit ganz heraus, und das ist richtig: für die
   * Kundenbetreuung quittiert nur die Bereichsleitung, und den
   * Rückgewinnungs-Stand trägt der **Vertrieb** ein. *Ein Mitarbeiter, den die
   * Liste ruft, ohne dass er handeln kann, macht die Liste unglaubwürdig.*
   */
  const nurVertriebGesperrt = !f.rueckgewinnung && rolle === "vertrieb";

  /**
   * ═══ 🔴 DER FÜNFTE SCHRITT — sonst ruft die Liste NIEMANDEN ════════════════
   *
   * 📏 Gefunden am 28.09.2026 auf Justins Frage *„sieht man dann auch optisch,
   * dass das ergebnis erstmal termin verschoben ist?"*: stehen alle vier Riegel
   * und die Rückgewinnung läuft, fällt `wartetAufStand` glatt durch und gibt
   * `null`. Der Fall ist **offen**, und die Liste sagt „nichts zu tun".
   * 🔑 ***Ein Zustand, den die Arbeitsliste nicht kennt, ist für sie erledigt.***
   *
   * ⚠️ **Nur wenn der Termin erreicht ist.** Vorher ist nichts zu tun, und ein
   * Ruf drei Monate vor dem Gespräch ist eine Mahnung ohne Anlass — *wer täglich
   * gerufen wird, ohne handeln zu können, hört auf hinzusehen.* Bis dahin steht
   * der Fall trotzdem als offener Posten in `offeneRiegel`; er verschwindet
   * nicht, er drängt nur nicht.
   *
   * 📌 Gerufen wird der **Vertrieb**: er hat das Gespräch geführt und trägt das
   * Ergebnis ein. *Eine Liste, die jemanden ruft, der nicht handeln kann, wird
   * unglaubwürdig.*
   */
  const wv = String(f.rueckgewinnung_wiedervorlage ?? "").trim();
  if (f.rueckgewinnung === "laeuft" && !f.storniert_am && !f.abgeschlossen_am
      && wv && wv <= heute && rolle === "vertrieb") {
    return {
      grund: `Die Wiedervorlage vom <b>${kDatum(wv)}</b> ist erreicht — `
        + "<b>das Ergebnis steht noch aus.</b> "
        + "Kündigung bleibt bestehen, zurückgewonnen, oder Termin verschieben.",
      cta: "Entscheiden"
    };
  }

  return wartetAufStand({
    storniert: !!f.storniert_am,
    abgeschlossen: !!f.abgeschlossen_am,
    vorbedingungOffen: nurVertriebGesperrt,
    offeneQuittungen: RIEGEL_PARTEIEN.filter((p) => !quittungAm(f, p)),
    vierterOffen: !f.bestaetigung_am,
    quittiert: (p) => !!quittungAm(f, p as Riegel),
    worte: {
      vorbedingung: ohneStand,
      vierterRiegel: {
        grund: "Alle drei Quittungen stehen. Es fehlt nur noch die "
          + "<b>Kündigungsbestätigung an den Verein</b> — der einzige Riegel mit Wirkung nach aussen.",
        cta: "Bestätigen"
      },
      /**
       * ⚠️ **Zwei Fassungen, weil es zwei Lagen sind.** Ohne Stand ist der Satz
       * „Rückgewinnung <b></b> steht" leer und sinnlos — und die eigentliche
       * Auskunft wäre unterschlagen: *dein* Haken ist nicht gesperrt.
       * 🔑 *Wer in einer Arbeitsliste steht, ohne zu erfahren, dass er handeln
       * DARF, sucht die Sperre bei sich.*
       */
      quittungFehlt: f.rueckgewinnung
        ? {
          // 🔴 28.09.2026: hier stand für JEDEN Wert „… <b>X</b> steht".
          // Bei `laeuft` heisst das Etikett „Entscheidung steht aus" — der Satz
          // las sich also als *„Entscheidung steht aus steht"*, und „steht"
          // heisst erledigt.
          // 🔑 ***Ein Satzbaustein, der jeden Wert einsetzt, behauptet über
          // jeden dasselbe*** — und über einen davon das Gegenteil.
          grund: f.rueckgewinnung === "laeuft"
            ? "Der Versuch läuft noch"
              + (wv ? ` (Wiedervorlage ${kDatum(wv)})` : "")
              + " — <b>deine Quittung fehlt</b>, sie hängt nicht daran."
            : `Ergebnis <b>${escapeHtml(rueckgewinnungLabel(f.rueckgewinnung))}</b> steht — `
              + "<b>deine Quittung fehlt.</b>",
          cta: "Quittieren"
        }
        : {
          grund: "<b>Deine Quittung fehlt.</b> Das Ergebnis steht noch aus — "
            + "er sperrt nur den <b>Vertriebs</b>-Haken, nicht deinen.",
          cta: "Quittieren"
        }
    }
  }, rolle);
}

/**
 * Dieselbe Frage für eine ÜBERSICHTSZEILE.
 *
 * 🔑 **Warum nicht dieselbe Funktion:** die Zeile ist kein Fall. Sie trägt den
 * Riegelstand als Werte (`riegel`) und das **wirksame** Ende aus dem Stamm,
 * während der Fall das Ende **bei der Anlage** führt.
 * ⚠️ Und das ist kein Detail: 📏 B1 hat am 25.09.2026 **24 Vertragsenden**
 * verschoben. Ein Band, das gegen `ende_bei_anlage` rechnet, spricht über ein
 * Ende, das nicht mehr gilt.
 */
export function wartetAufZeile(z: RegisterZeile, rolle: WRolle, heute: string): Wartend | null {
  /**
   * ═══ 🔴 OHNE VORGANG WARTET NIEMAND ════════════════════════════════════
   *
   * 📏 Am Bildschirm gemessen, 26.09.2026, unmittelbar nach dem Deploy:
   * **19 Zeilen unter „Bei mir" — davon trugen genau ZWEI einen Fall.** Die
   * anderen 17 hatten keinen Vorgang: keine Karte mit Link, keine Riegel-Spur,
   * keine Marke — und wurden trotzdem gezählt und gerufen.
   *
   * Die Ursache: ohne Vorgang sind **alle** Felder in `z.riegel` leer, also
   * auch `rueckgewinnung`. Die Rollenfrage liest daraus „die Vorbedingung
   * fehlt" und ruft den Vertrieb. Sie hat richtig gerechnet — **an der
   * falschen Menge**.
   * 🔑 ***Eine richtige Messung an der falschen Menge ist nicht halb richtig,
   * sondern falsch*** — und sie sieht dabei genauso aus wie eine richtige.
   *
   * ⚠️ „Nichts eingetragen" und „noch nichts begonnen" dürfen nicht dasselbe
   * bedeuten. Der Typ sagt es selbst über `offene_riegel`: *leer, solange es
   * zum Verein gar keinen Vorgang gibt — dort ist nichts offen, sondern noch
   * nichts begonnen.* Hier stand dieselbe Unterscheidung nicht.
   *
   * ⛔ Die Zeile verschwindet dadurch **nicht**: sie fällt in „Weiterhin offen",
   * und die Regelzeile dort nennt den Fall. *Ein Rückstau, den niemand zählt,
   * ist schlimmer als einer, der falsch gezählt wird.*
   */
  if (!z.fall_id) return null;
  const r = z.riegel;
  return wartetAufKuendigung({
    ende_bei_anlage: z.gekuendigt_zum || z.ende_ist,
    rueckgewinnung: r.rueckgewinnung,
    rueckgewinnung_wiedervorlage: r.rueckgewinnung_wiedervorlage,
    quittung_vertrieb: r.vertrieb, quittung_kb: r.kb, quittung_finance: r.finance,
    bestaetigung_am: r.bestaetigung,
    storniert_am: r.storniert, abgeschlossen_am: r.abgeschlossen
    // ⚠️ Kein `as`: `KuendigungStand` zählt die Felder auf, und ein neuntes
    // Feld bricht **hier**, nicht im Betrieb.
  }, rolle, heute);
}

/**
 * Das Band über der Liste — **1:1 `mineBand` des Widerrufs**: die Zahl bleibt
 * im Kopf, die Zeilen klappen weg.
 *
 * 🔑 *Ein eingeklappter Abschnitt darf seine MENGE nie mit einklappen* — sonst
 * ist der Rückstau versteckt statt aufgeräumt.
 * ⚠️ Der leere Zustand bleibt ein `<div>`: *ein Aufklapp-Knopf vor einem leeren
 * Fach verspricht einen Inhalt, den es nicht gibt.*
 */
export function kMineBand(
  zeilen: readonly RegisterZeile[], rolle: WRolle, heute: string, basis: string
): string {
  const meine = zeilen
    .map((z) => ({ z, w: wartetAufZeile(z, rolle, heute) }))
    .filter((x): x is { z: RegisterZeile; w: Wartend } => !!x.w);

  const rolleText = rolle === "vertrieb" ? "Vertrieb"
    : rolle === "finance" ? "Finance"
    : rolle === "kb" ? "Kundenbetreuung" : "Kundenbetreuung (ohne Bereichsleitung)";

  if (!meine.length) {
    return `<div class="w-mine is-clear">
      <div class="w-mine-head">
        <h2>Auf dich wartet <b>nichts</b></h2>
        <span class="task">${escapeHtml(rolleText)}</span>
      </div>
      <div class="w-mine-none">Kein offener Vorgang braucht gerade eine Handlung von dir.
        Die Abschnitte darunter bleiben trotzdem lesenswert: dort steht, wo noch etwas
        kommen kann.</div>
    </div>`;
  }

  // Das nächste wirksame Vertragsende als Uhr im Kopf — die kleinste vorhandene,
  // nicht gerechnet. 🔑 *Ohne Ende kein Eintrag, kein Ersatzwert.*
  // 🔴 05.10.2026 (Klicktest, wie V01·W2 im Widerruf) — nur KÜNFTIGE Enden: am
  // Schirm stand „nächstes Ende 30.09.2026" am 05.10. Ein erreichtes Ende steht
  // schon in der Kachel „Vertragsende erreicht".
  const enden = meine.map((x) => x.z.gekuendigt_zum || x.z.ende_ist)
    .filter((d) => !!d && d >= heute).sort();
  const naechstes = enden[0] ?? "";

  return `<details class="w-mine">
    <summary class="w-mine-head">
      <h2>Auf dich wartet <b>${meine.length}</b></h2>
      <span class="task">${escapeHtml(rolleText)}</span>
      ${naechstes ? `<span class="clock">nächstes Ende ${escapeHtml(appFormatDate(naechstes))}</span>` : ""}
    </summary>
    <div class="w-mine-rows">
      ${meine.map(({ z, w }) => `
        ${/*
          🔄 26.09.2026 — die Basis kommt als PARAMETER, nicht aus `location`.
          📏 Gefunden von einem neuen Test: `kMineBand` warf `ReferenceError:
          location is not defined`. Der Aufrufer hat die Basis längst.
          🔑 *Eine Zeichenfunktion, die nach einer globalen Variablen greift, ist
          nicht nur schwer prüfbar — sie liest die Adresse zum Zeitpunkt des
          Zeichnens statt zum Zeitpunkt der Entscheidung, und die beiden fallen
          beim ersten Reiterwechsel auseinander.*
        */ ""}
        <a class="w-mrow" href="${escapeHtml(`${basis}/fall/${z.fall_id}`)}">
          <div>
            <div class="club">${escapeHtml(z.verein || z.company_id)}</div>
            <div class="why">${w.grund}</div>
          </div>
          <div class="right"><span class="ts-btn ts-btn-sm">${escapeHtml(w.cta)}</span></div>
        </a>`).join("")}
    </div>
  </details>`;
}

/**
 * Das Aufruf-Band — **dieselbe Gestalt wie `callBand` im Widerruf**: eine Marke,
 * ein Satz, ein Knopf.
 *
 * 🔑 Die vier Marken sind dieselben vier Lagen: *Storniert · Geschlossen ·
 * Letzter Riegel · Du bist dran / Nicht du.*
 */
export function kBand(f: KuendigungFall, rolle: WRolle, heute: string): string {
  if (f.storniert_am) {
    return `<div class="w-call is-quiet"><span class="tag">Zurückgenommen</span>
      <span class="txt">Der Vorgang wurde am <b>${escapeHtml(appFormatDate(f.storniert_am))}</b>
      aus der Arbeitsliste genommen. Grund: ${escapeHtml(f.storno_grund || "—")}
      ⚠️ In HubSpot stehen die Kündigungsfelder womöglich noch — das Register ändert die
      Quelle nicht.</span></div>`;
  }
  if (f.abgeschlossen_am) {
    // 🔄 05.10.2026 (V03, Regel „Bestätigung", README §1): nicht „alle vier
    // Riegel" — die Bestätigung ist keine vierte Quittung. Satzbau wie
    // `callBand` im Widerruf, auch für Altfälle ohne Bestätigung.
    return `<div class="w-call is-quiet"><span class="tag">Abgewickelt</span>
      <span class="txt">${f.bestaetigung_am
    ? "Alle drei Quittungen und die Kündigungsbestätigung liegen vor"
    : "Alle drei Quittungen liegen vor"} — abgewickelt am
      <b>${escapeHtml(appFormatDate(f.abgeschlossen_am))}</b>.
      🔑 Der Vertrag kann weiterlaufen: die Abwicklung ist Arbeit und hat einen Abschluss,
      das Vertragsende ist ein Kalendertag.</span></div>`;
  }

  const w = wartetAufKuendigung(f, rolle, heute);
  if (!w) {
    const offen = RIEGEL_PARTEIEN.filter((p) => !quittungAm(f, p)).map((p) => RIEGEL_LABEL[p]);
    if (!f.bestaetigung_am) offen.push("Bestätigung an den Verein");
    const wen = offen.length ? offen.join(" und ") : "niemanden mehr";
    const zusatz = rolle === "kb_ma" && f.rueckgewinnung
      ? " Die Quittung der Kundenbetreuung setzt <b>die Bereichsleitung</b> — dieser Vorgang wartet nicht auf dich."
      : "";
    return `<div class="w-call is-quiet"><span class="tag">Nicht du</span>
      <span class="txt">Der Vorgang wartet auf <b>${escapeHtml(wen)}</b>.${zusatz}</span></div>`;
  }

  const letzter = !f.bestaetigung_am
    && RIEGEL_PARTEIEN.every((p) => quittungAm(f, p));

  /**
   * ═══ 🆕 04.10.2026 — K4: DER KNOPF GEHÖRT INS BAND ═════════════════════════
   *
   * Das Band sagte, dass du dran bist — der Knopf dazu stand erst in der Karte
   * darunter. Der Widerruf setzt ihn seit jeher rechts ins Band.
   * 🔑 ***Ein Aufruf ohne den Weg zur Handlung verlangt vom Leser, sie zu
   * suchen — und er sucht genau einmal.***
   *
   * ⚠️ **Derselbe `data-k-q`-Haken wie in der Parteikarte**, nicht ein zweiter
   * Weg: der Zuhörer liegt auf `[data-k-q]` und fasst beide. *Zwei Knöpfe mit
   * demselben Zweck und verschiedener Verdrahtung driften beim ersten Umbau.*
   * ⛔ Fehlt das **Ergebnis**, steht dort „Ergebnis melden" (E1) — ein Verweis
   * auf den Block, kein zweiter Schreibweg.
   */
  const meine = rolle === "vertrieb" ? "vertrieb" : rolle === "kb" ? "kb"
    : rolle === "finance" ? "finance" : null;
  const knopf = meine && !quittungAm(f, meine as Riegel)
    && !(meine === "vertrieb" && !f.rueckgewinnung)
    ? `<button class="ts-btn ts-btn-primary ts-btn-sm" data-k-q="${escapeHtml(meine)}"
        >Als ${escapeHtml(RIEGEL_LABEL[meine as Riegel])} quittieren</button>`
    : !f.rueckgewinnung
      ? `<button class="ts-btn ts-btn-primary ts-btn-sm" data-k-scroll="k-ergpick"
          >Ergebnis melden</button>`
      : "";

  return `<div class="w-call"><span class="tag">${letzter ? "Letzter Riegel" : "Du bist dran"}</span>
    <span class="txt">${w.grund}</span>
    <span class="go">${knopf}</span></div>`;
}

/**
 * Die `data-du`-Marke einer Karte — nur die EIGENE Rolle.
 * 🔑 *Eine Marke für eine Rolle, die man nicht hat, wäre Anzeige ohne Adressaten.*
 *
 * ⚠️ Es gibt sie **nur** für die Übersichtszeile. Eine zweite Fassung für den
 * Fall gab es kurz — ohne Aufrufer und ohne Test.
 * 🔑 *Eine ungenutzte Fassung derselben Regel ist die, die als Erste falsch wird.*
 */
export function kDuMarkeZeile(z: RegisterZeile, rolle: WRolle, heute: string): string {
  if (!wartetAufZeile(z, rolle, heute)) return "";
  return rolle === "vertrieb" ? "v" : rolle === "kb" ? "kb" : rolle === "kb_ma" ? "kbma" : "fin";
}

// ═══════════════════════════════════════════════════════════════════════════
// DIE RIEGEL-SPUR — die dritte Spalte der Karte
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die vier Riegel als Spur, **1:1 `spur()` des Widerrufs** (dieselben Klassen
 * `w-spur` / `w-q` / `q-v` `q-kb` `q-fin`, damit die `data-du`-Einfärbung
 * greift).
 *
 * ═══ 🔴 WAS DIE KÜNDIGUNGS-KARTE STATTDESSEN HATTE ═════════════════════════
 * 📏 Bis zum 26.09.2026: die Zeile `offen: Vertrieb, KB, Finance` — eine
 * Aufzählung der **Beschriftungen** aus `offene_riegel`.
 * 🔑 ***Eine Liste dessen, was fehlt, sagt nicht, was steht.*** Man sah nicht,
 * ob Vertrieb schon quittiert hatte, nicht wann, und die Einfärbung „wartet auf
 * dich" hatte nichts, woran sie greifen konnte.
 *
 * ⚠️ **VIER Zellen, nicht drei.** Der vierte Riegel ist die Bestätigung an den
 * Verein — der einzige mit Wirkung nach aussen. Er hat keine eigene
 * `data-du`-Klasse, weil ihn nicht eine Rolle schuldet: das entscheidet
 * `KUENDIGUNG_BESTAETIGUNG_WER`. *Eine Farbe für einen Adressaten, den der
 * Schalter bestimmt, wäre eine Behauptung über den Schalterstand.*
 *
 * 🔒 Gesperrt, solange kein Rückgewinnungs-Stand da ist — derselbe Grund wie
 * beim Widerruf ohne Ergebnis: es fehlt nicht die Berechtigung, sondern der
 * **Gegenstand**.
 */
const K_SPUR: ReadonlyArray<{ kurz: string; kl: string; von: (r: RegisterZeile["riegel"]) => string }> = [
  { kurz: "Vertrieb", kl: "q-v", von: (r) => r.vertrieb },
  // 🔄 05.10.2026 — „KB" statt „Betreuung", wie im Widerruf (`w-rahmen.ts`) und im Zielbild.
  { kurz: "KB", kl: "q-kb", von: (r) => r.kb },
  { kurz: "Finance", kl: "q-fin", von: (r) => r.finance }
  /**
   * 🔴 **05.10.2026 — die vierte Zelle „Bestätigung" ist raus.** Drei
   * Quittungen, wie im Widerruf (Justin: *„4 quittierungskacheln … wovon nur 3
   * da sein sollten wie in Widerrufe auch"*).
   * Der Glattzug (04.10.) hatte den ZÄHLER berichtigt — die Bestätigung an den
   * Verein ist *keine vierte Quittung* — und die Zelle daneben stehen lassen.
   * 🔑 *Wer die Zahl berichtigt und die Kachel stehen lässt, behauptet dieselbe
   * Gleichartigkeit weiter, nur ohne Ziffer.* Die Bestätigung steht im
   * Falldetail in ihrem eigenen Block.
   */
];

export function kSpur(z: RegisterZeile): string {
  // ⚠️ Kein Vorgang ⇒ keine Spur. *Drei leere Kästchen sähen aus wie drei
  // offene Riegel — dabei hat hier noch nichts begonnen.*
  if (!z.fall_id) return "";
  const r = z.riegel;
  /**
   * ═══ 🔴 GESPERRT IST EINE ZELLE, NICHT DIE SPUR ════════════════════════
   *
   * 📏 Bis zum 26.09.2026 trug die ganze Spur `is-locked`, solange kein
   * Rückgewinnungs-Stand dastand, mit dem Satz „Quittieren geht erst, wenn ein
   * Rückgewinnungs-Stand eingetragen ist."
   * ⛔ **Das stimmt nur für den Vertrieb.** Der Server prüft
   * `riegel === "vertrieb" && !fall.rueckgewinnung` — KB und Finance dürfen
   * jederzeit. Die Sperre war aus dem Widerruf übernommen, wo sie für alle gilt.
   * 🔑 ***Eine Sperre, die zu breit angezeigt wird, hält Leute von Arbeit ab,
   * die sie tun dürfen — und sie sieht dabei aus wie Sorgfalt.***
   */
  const vertriebGesperrt = !r.rueckgewinnung;
  /**
   * 🔄 04.10.2026 (E6) — der gesperrte Vertriebs-Chip sagt **„Ergebnis fehlt"**
   * statt „wartet auf Stand" (Zielbild `01-bei-mir__kuendigung__1440.png`).
   *
   * ⛔ **Und er bekommt dafür KEINE eigene Lime-Regel.** Die Vorgabe schreibt
   * „in Lime, wenn gesperrt" — lime wird er aber schon heute, und zwar über
   * `[data-role] .w-case[data-du~=…]`, also **nur für den, der dran ist**.
   * Eine zweite Regel „lime, weil gesperrt" träfe auch die Kundenbetreuung,
   * die hier gar nichts zu tun hat — und bräche damit **E19** („Lime nur für
   * ‚Für dich'").
   * 🔑 *Zwei Regeln für dieselbe Farbe sind eine Farbe ohne Aussage.*
   */
  const zellen = K_SPUR.map(({ kurz, kl, von }) => {
    const am = von(r);
    const wartet = !am && kl === "q-v" && vertriebGesperrt;
    return `<span class="w-q ${kl} ${am ? "is-done" : "is-open"}">
      <span class="p">${escapeHtml(kurz)}</span>
      <span class="st">${am
      ? `${HAKEN}${escapeHtml(appFormatDate(am.slice(0, 10)))}`
      : wartet ? "Ergebnis fehlt" : "offen"}</span>
    </span>`;
  }).join("");
  const spur = `<div class="w-spur">${zellen}</div>`;
  if (!vertriebGesperrt) return spur;
  return `<div>${spur}<span class="w-spurnote">Nur der <b>Vertriebs</b>-Haken wartet auf
    das Ergebnis.</span></div>`;
}

// ═══════════════════════════════════════════════════════════════════════════
// DIE ARBEITSREIHENFOLGE
// ═══════════════════════════════════════════════════════════════════════════

/** Das **wirksame** Ende einer Zeile — gekündigt zum, sonst der Stamm. */
export function kEnde(z: RegisterZeile): string {
  return z.gekuendigt_zum || z.ende_ist || "";
}

/**
 * Der Rang einer Zeile in der Arbeitsliste.
 *
 * 🔑 ***Sortiert nach Beeinflussbarkeit, nicht nach Schwere*** — übernommen
 * samt Begründung von `dringlichkeit()` im Widerruf.
 *
 * ⚠️ **Was „beeinflussbar" heisst, ist hier etwas anderes.** Beim Widerruf
 * läuft eine Bearbeitungsfrist: solange sie läuft, kann man den Widerruf noch
 * prüfen. Bei der Kündigung läuft das **Vertragsende** — und ein
 * Rückgewinnungs-Versuch wirkt **nur davor**. Danach ist der Verein weg, und
 * es bleibt das Nachtragen.
 * 🔑 *Dieselbe Rangfolge, ein anderer Grund: nicht die Frist ist knapp,
 * sondern die Gelegenheit.*
 *
 * ① Ende in der Zukunft, kein Rückgewinnungs-Stand ⇒ **hier ist noch etwas zu
 *    holen**, das nächste Ende zuerst
 * ② Ende erreicht, Stand fehlt weiter ⇒ überfällig, nur noch nachzutragen
 * ③ **kein** Ende ⇒ eigener Rang: *ein leerer Wert ist keine Sortiergrundlage,
 *    er ist ein eigener Fall* — nach Kündigungsdatum, ältestes zuerst
 * ④ Stand steht, es fehlen nur Quittungen ⇒ Kenntnisnahme
 *
 * ⛔ Die Fall-ID bleibt der **letzte** Schlüssel: sonst wäre die Reihenfolge
 * bei gleichem Ende von der Datenbank abhängig und an zwei Tagen verschieden,
 * ohne dass sich etwas geändert hat.
 */
export function kDringlichkeit(z: RegisterZeile, heute: string): [number, string, string] {
  const ende = kEnde(z);
  if (z.riegel.rueckgewinnung) return [4, ende || "9999", z.fall_id];
  if (!ende) return [3, z.gekuendigt_am || "9999", z.fall_id];
  return ende >= heute ? [1, ende, z.fall_id] : [2, ende, z.fall_id];
}

/** Die Liste in Arbeitsreihenfolge. Verändert die Eingabe NICHT. */
export function nachKDringlichkeit(
  zeilen: readonly RegisterZeile[], heute: string
): RegisterZeile[] {
  return [...zeilen].sort((a, b) => {
    const [ra, sa, ia] = kDringlichkeit(a, heute);
    const [rb, sb, ib] = kDringlichkeit(b, heute);
    return ra - rb || sa.localeCompare(sb) || ia.localeCompare(ib);
  });
}

/** Der Satz, der die Reihenfolge erklärt — an EINER Stelle wie die Regel. */
export const K_DRINGLICHKEIT_REGEL =
  `Sortiert nach <b>Dringlichkeit</b>: Vertrag läuft noch, Ergebnis offen (nächstes `
  + `Ende zuerst) · Ende erreicht, Ergebnis fehlt · ohne Vertragsende (älteste Kündigung) · `
  + `Ergebnis liegt vor. 🔑 Der laufende Vertrag steht über dem beendeten — `
  + `<b>eine Arbeitsliste sortiert nach Beeinflussbarkeit, nicht nach Schwere</b>, und `
  + `zurückgewinnen kann man nur <b>vor</b> dem Ende.`;

/**
 * DER HINWEISKASTEN — **eine Form, nicht nur ein Stilname.**
 *
 * 🔴 `.w-datenhinweis` ist `display: flex` mit `gap: 12px` und erwartet **zwei**
 * Kinder: den Balken und EIN Element mit dem Text. 📏 Am 26.09.2026 legten acht
 * Stellen im Falldetail den Text **nackt** hinein — jedes `<b>` wurde damit zu
 * einem eigenen Flex-Item, und die Kästen brachen **wortweise in Spalten**.
 * 🔑 ***Ein Stilname allein genügt nicht — das CSS erwartet auch eine FORM.***
 *
 * 🆕 **28.09.2026 aus `kuendigung-detail.ts` hierher gezogen**, weil die
 * Regel-Bedienform ihn jetzt an zwei Orten braucht.
 * 🔑 *Eine Form, die man jedes Mal selbst herstellen muss, stellt man irgendwann
 * nicht her* — wer `kHinweis()` benutzt, kann sie nicht falsch bauen.
 */
export function kHinweis(inhalt: string): string {
  return `<div class="w-datenhinweis"><div class="bar"></div><div>${inhalt}</div></div>`;
}

/** Datum für die Anzeige. Schneidet einen Zeitstempel ab; leer ⇒ Gedankenstrich. */
export function kDatum(ymd: string): string {
  return ymd ? appFormatDate(ymd.slice(0, 10)) : "—";
}
